import { useEffect, useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { Toaster } from '@/components/ui/toaster'
import { ChatPage } from '@/pages/ChatPage'
import { LoginPage } from '@/pages/LoginPage'
import { AdminPage } from '@/pages/AdminPage'
import { LibraryPage } from '@/pages/LibraryPage'
import { OutlookAddinPage } from '@/addins/outlook/OutlookAddinPage'
import { authService } from '@/lib/auth'
import { apiClient } from '@/lib/api'
import { AuthConfig, HealthResponse } from '@/lib/types'
import { configService } from '@/lib/config-service'

function App() {
  const { instance, accounts } = useMsal()
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null)
  const [healthStatus, setHealthStatus] = useState<HealthResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authInitialized, setAuthInitialized] = useState(false)
  const [authStateVersion, setAuthStateVersion] = useState(0)
  const [templateLibraryEnabled, setTemplateLibraryEnabled] = useState(true)

  useEffect(() => {
    initializeApp()
  }, [])


  const initializeApp = async () => {
    try {
      // Get auth configuration
      const config = await apiClient.getAuthConfig()
      setAuthConfig(config)

      // Get health status
      const health = await apiClient.getHealth()
      setHealthStatus(health)

      // Get Template Library feature flag
      try {
        // Clear cache to ensure fresh config
        configService.clearCache()
        const enabled = await configService.isTemplateLibraryEnabled()
        setTemplateLibraryEnabled(enabled)
      } catch (error) {
        console.warn('Failed to get Template Library feature flag, defaulting to enabled:', error)
        setTemplateLibraryEnabled(true)
      }

      // Authentication is always required now
      if (!config.auth_enabled) {
        throw new Error('Authentication is required but not configured')
      }

      try {
        // Set the MSAL instance in the auth service
        authService.setMsalInstance(instance)

        // Set up auth state change callback to force re-render
        authService.setOnStateChange(() => {
          setAuthStateVersion(prev => prev + 1)
        })

        // Handle any redirect results first
        await instance.handleRedirectPromise()

        // Wait for auth service to initialize (this will handle the callback)
        await authService.waitForInitialization()

        // Get token if user is authenticated
        const token = await authService.getToken()
        if (token) {
          apiClient.setAuthToken(token)
        }
      } catch (error) {
        console.warn('Failed to get auth token:', error)
      }
      
      setAuthInitialized(true)
    } catch (error) {
      console.error('Failed to initialize app:', error)
      setAuthInitialized(true)
    } finally {
      setIsLoading(false)
    }
  }


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="spinner w-8 h-8"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  if (!healthStatus || healthStatus.status !== 'ok') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold text-destructive">Service Unavailable</h1>
          <p className="text-muted-foreground">
            The AI chat service is currently unavailable. Please try again later.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Check if we're in the callback flow
  const isInCallback = window.location.pathname === '/auth/callback'
  
  // Authentication is always required (authStateVersion forces re-evaluation when auth state changes)
  const isAuthenticated = authInitialized && authService.isAuthenticated() && authStateVersion >= 0
  

  return (
    <div className="min-h-screen bg-background">
      <Routes>
        <Route
          path="/addin/outlook"
          element={<OutlookAddinPage />}
        />
        <Route 
          path="/login" 
          element={
            !isAuthenticated ? (
              <LoginPage authConfig={authConfig!} />
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
        <Route 
          path="/auth/success"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="spinner w-8 h-8"></div>
                  <p className="text-muted-foreground">Completing authentication...</p>
                </div>
              </div>
            )
          }
        />
        <Route 
          path="/auth/callback"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="flex flex-col items-center space-y-4">
                  <div className="spinner w-8 h-8"></div>
                  <p className="text-muted-foreground">Completing authentication...</p>
                </div>
              </div>
            )
          }
        />
        <Route 
          path="/admin" 
          element={
            isAuthenticated ? (
              <AdminPage />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
        {templateLibraryEnabled && (
          <Route 
            path="/library" 
            element={
              isAuthenticated ? (
                <LibraryPage />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        )}
        <Route 
          path="/" 
          element={
            isAuthenticated ? (
              <ChatPage 
                authConfig={authConfig!}
                healthStatus={healthStatus}
              />
            ) : (
              <Navigate to="/login" replace />
            )
          } 
        />
      </Routes>
      <Toaster />
    </div>
  )
}

export default App

